import _ from 'lodash';
import Immutable from 'seamless-immutable';
import { assetRefService } from '../../common/Services';
import { prepCategoryLabel, extractParamsFromQueryString } from '../../common/Utils.js';
import { getUserActionElements, determineCtaDetails } from '../../AppStore/utils.js';

/**
 * App card is used in the app store and the app create project.
 * @typedef {Object} AppCard
 * @property {string} appId - the app id is a definition of the app itself used to navigate to the appId home page (for example giveway, print-coupon, etc)
 * @property {string} appType - if the experience is a store experience or an svg package (check for details RefData.Enum.STOREAPP)
 * @property {string} actionRef - a string nav ref used to navigate to the apps landing page when selecting the card, generally in the form of: route:app/<category apps id>
 * @property {string} assetRef - a simple ref that hosts the apps background image for the card
 * @property {string} image - an image string ref in the shape sysui:image:450x253:/store/<category name>/thumbnail.jpg used to generated the exact background image for the card
 * @property {array} keyCategories - an array of category strings, these are designed to showcase a quick brief of the experiences features and are displayed via color lozenge in KeyCategoryLozenge.jsx
 * @property {array} keyCategoryLabels - a human readable set of labels to be used displayed in the lozenge on category card hover
 * @property {string} name - the header title copy text
 * @property {string} summary - a short description for the experience template
 * @property {string} typeRef - a simple string generally used to generate a CK icon to represent the card
 * @property {number} id - the database reference for the experience template, mostly use in the wizards to fetch the full template data
 * @property {array} actions - an array of actions that can be taken on the experience template, such as "preview experience"
 * @property {boolean} added - a flag indicating if the app has been added to the user's account
 */

/**
 * Transform a BE instance of an into a FE app card model
 * @return {Object}  AppCard
 */
function deserialize(appDetails) {
  const result = _.pick(appDetails, _keys);

  result.image = assetRefService.pathFromAssetRef(result.assetRef);
  result.actionRef = `route:app/${result.appId}`;
  result.icon = appDetails.typeRef === 'default' ? 'app' : appDetails.typeRef;

  // cta details for the cta button (create, request pricing info etc)
  const { id, appId: friendlyId, appType, name: title, added } = result;
  result.cta = determineCtaDetails({ id, friendlyId, appType, title, added });

  // find the preview link from the actions array, this is needed to link the preview button on the card to the correct experience preview page
  result.previewLink = null;
  if (result.actions) {
    result.actions.forEach((action) => {
      const [userActionType, actionDestination] = getUserActionElements(action.actionRef);
      if (userActionType === 'url') result.previewLink = actionDestination;
    });
  }

  //turns all technical keyCategories into human readable category text (replacing - and & with spaces or renaming micro experiences to nextgen)
  //WARNING! this is not supported on all app cards
  if (result.keyCategories)
    result.keyCategoryLabels = result.keyCategories.map((keyCategory) => prepCategoryLabel(keyCategory));

  return Immutable(result);
}

const _keys = [
  'relatedExperiences',
  'appId',
  'appType',
  'assetRef',
  'keyCategories',
  'name',
  'summary',
  'typeRef',
  'id',
  'actions',
  'added',
];

export default {
  deserialize,
};
